import React, { useState } from "react";
import { createPortal } from "react-dom";
import { DndContext, closestCenter, DragOverlay } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { Item, Wrapper } from "./components";

export function Sortable({
  items = [],
  onItemsReorder,
  onItemRemove,
  removable = false,
  adjustScale = false,
  animateLayoutChanges,
  getItemStyles = () => ({}),
  handle = false,
  measuring,
  modifiers,
  renderItem,
  isPreviewMode,
  resources,
}) {
  const [activeId, setActiveId] = useState(null);

  const getIndex = (id) => items.indexOf(id);
  const activeIndex = activeId != null ? getIndex(activeId) : -1;

  const handleRemove = removable
    ? (id) => {
        if (onItemRemove) {
          onItemRemove(id);
        }
      }
    : undefined;

  return (
		<DndContext
			collisionDetection={closestCenter}
			onDragStart={({ active }) => {
				if (!active) {
					return;
				}
				setActiveId(active.id);
			}}
			onDragEnd={({ over }) => {
				setActiveId(null);
				if (over) {
					const overIndex = getIndex(over.id);
					if (activeIndex !== overIndex) {
						const newOrder = arrayMove(items, activeIndex, overIndex);
						if (onItemsReorder) {
							onItemsReorder(newOrder);
						}
					}
				}
			}}
			onDragCancel={() => setActiveId(null)}
			measuring={measuring}
			modifiers={modifiers}
		>
			<Wrapper center>
				<SortableContext items={items} strategy={rectSortingStrategy}>
					{items.map((value, index) => (
						<SortableItem
							key={value}
							id={value}
							index={index}
							onRemove={handleRemove}
							animateLayoutChanges={animateLayoutChanges}
							handle={handle}
							renderItem={renderItem}
							style={getItemStyles}
							isPreviewMode={isPreviewMode}
							resources={resources}
						/>
					))}
				</SortableContext>
			</Wrapper>

			{createPortal(
				<DragOverlay adjustScale={adjustScale} style={{ zIndex: 9999 }}>
					{activeId != null && activeIndex !== -1 ? (
						<Item
							value={items[activeIndex]}
							handle={handle}
							renderItem={renderItem}
							onRemove={handleRemove}
							style={getItemStyles({
								id: items[activeIndex],
								index: activeIndex,
								isSorting: activeId !== null,
								isDragging: true,
								overIndex: -1,
								isDragOverlay: true,
							})}
							dragOverlay
							resources={resources}
						/>
					) : null}
				</DragOverlay>,
				document.body
			)}
		</DndContext>
	);
}

export function SortableItem({
  disabled,
  animateLayoutChanges,
  handle,
  id,
  index,
  onRemove,
  style,
  renderItem,
  isPreviewMode,
  resources,
}) {
  const {
    attributes,
    isDragging,
    isSorting,
    listeners,
    overIndex,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    animateLayoutChanges,
    disabled,
  });

  return (
		<Item
			ref={setNodeRef}
			value={id}
			disabled={disabled}
			dragging={isDragging}
			sorting={isSorting}
			handle={handle}
			handleProps={
				handle
					? {
							ref: setActivatorNodeRef,
					  }
					: undefined
			}
			renderItem={renderItem}
			index={index}
			style={style({
				index,
				id,
				isDragging,
				isSorting,
				overIndex,
			})}
			onRemove={onRemove ? () => onRemove(id) : undefined}
			transform={transform}
			transition={transition}
			listeners={listeners}
			data-index={index}
			data-id={id}
			dragOverlay={isDragging}
			isPreviewMode={isPreviewMode}
			resources={resources}
			{...attributes}
		/>
	);
}
